import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const CONSENT_KEY = 'cookieConsent';

export const getCookieConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null');
  } catch {
    return null;
  }
};

// Bottom consent banner shown until the visitor makes a choice.
// "Accept" allows optional cookies (analytics etc. when added later);
// "Essential only" limits us to what the app needs to function
// (login session, saved preferences). The choice is remembered.
const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!getCookieConsent()) {
      // Small delay so it slides in after the page settles
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  const decide = (choice) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
    } catch { /* storage unavailable — just hide */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 'calc(16px + env(safe-area-inset-bottom))',
        transform: 'translateX(-50%)',
        zIndex: 9998,
        width: 'calc(100% - 24px)',
        maxWidth: '560px',
        backgroundColor: '#fff',
        border: '1px solid var(--gray-4)',
        borderRadius: '16px',
        boxShadow: '0 20px 50px -12px rgba(0,0,0,0.25)',
        padding: '16px 18px',
        fontFamily: "'Poppins', sans-serif",
        animation: 'cookieSlideUp 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      <div className="flex items-start gap-3">
        <span style={{ fontSize: '22px', lineHeight: 1, flexShrink: 0 }} aria-hidden>🍪</span>
        <div className="flex-1 min-w-0">
          <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)', margin: '0 0 2px', textAlign: 'left' }}>
            We value your privacy
          </p>
          <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, lineHeight: 1.55, textAlign: 'left' }}>
            We use cookies and similar storage to keep you signed in, remember your preferences,
            and improve the experience. Read our{' '}
            <Link to="/cookies" style={{ color: 'var(--orange-9)', fontWeight: 600, textDecoration: 'underline' }}>
              Cookie Policy
            </Link>.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mt-3 justify-end flex-wrap">
        <button
          type="button"
          onClick={() => decide('essential')}
          style={{
            padding: '9px 16px', borderRadius: '10px',
            border: '1.5px solid var(--gray-5)', background: 'transparent',
            fontSize: '12px', fontWeight: 600, color: 'var(--gray-11)',
            cursor: 'pointer', fontFamily: "'Poppins', sans-serif",
          }}
        >
          Essential only
        </button>
        <button
          type="button"
          onClick={() => decide('accepted')}
          style={{
            padding: '9px 20px', borderRadius: '10px', border: 'none',
            background: 'linear-gradient(135deg, var(--orange-9), #f97316)',
            fontSize: '12px', fontWeight: 600, color: '#fff',
            cursor: 'pointer', fontFamily: "'Poppins', sans-serif",
            boxShadow: '0 4px 12px rgba(240,90,53,0.35)',
          }}
        >
          Accept all
        </button>
      </div>

      <style>{`
        @keyframes cookieSlideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(24px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default CookieConsent;
